import React, { useContext, useEffect, useMemo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import Heading from '@legacy-vfuk/core-heading';
import { Container } from '@legacy-vfuk/core-container';
import { SimpleGrid } from '@legacy-vfuk/core-simple-grid';
import { Icon } from '@legacy-vfuk/core-icon';
import { Pill } from '@legacy-vfuk/core-pill';
import { Link } from 'gatsby';
import {
  ArticleClickEventData,
  ArticleProps,
  CustomWindow,
} from './types/Article.types';
import replaceSlug from '../../../../helpers/replaceSlugs';
import reduceLookUpTable from '../../../../helpers/reduceLookUpTable';
import {
  ArticleWrapper,
  imageBlock,
  focalPointImageStyle,
  TextBlock,
  TagsBarWrapper,
  InfoWrapper,
  IconWithTextWrapper,
  ImageContainer,
  TagPillWrapper,
} from './styles/Article.styles';
import { DirectionContext } from '../../../../contexts/DirectionContext';

const queryLookupData = graphql`
  {
    contentfulSlugCharacterLookup {
      name
      exceptions
      lookup {
        lookup
      }
    }
  }
`;

declare const window: CustomWindow;

const fireArticleClickEvent = (trackingData: ArticleClickEventData) => {
  if (window.onArticleClickEvent) {
    try {
      // fire the utag link event
      window.onArticleClickEvent(trackingData);
    } catch (e) {
      console.log(
        'onArticleClickEvent failed - please check the Article Clicks SEO script'
      );
    }
  }
};

const sortByMatch = (categories, queryCategories) => {
  const result = categories.slice();

  result.sort((a, b) => {
    const matchA = queryCategories.includes(a);
    const matchB = queryCategories.includes(b);

    if (matchA && !matchB) {
      return -1;
    } else if (!matchA && matchB) {
      return 1;
    } else {
      return 0;
    }
  });

  return result.slice(0, 2);
};

export const Article = ({
  data,
  indexSlug,
  backgroundColor,
  seoTitle,
  appliedFilters,
  queryCategories = [],
  viewCount,
}: ArticleProps) => {
  const {
    pageArticleName: name,
    mainImage,
    articleThumbnailImage,
    summary,
    slug,
    publishingDate,
    categoryTags,
    topicTags,
    contentType,
    readingTime,
  } = data;

  // Replace characters within the slug, using the Slug character look up from Contentful
  const {
    contentfulSlugCharacterLookup,
  } = useStaticQuery(queryLookupData);

  const direction = useContext(DirectionContext);

  const exceptions = contentfulSlugCharacterLookup?.exceptions;
  const lookup = contentfulSlugCharacterLookup?.lookup?.lookup;

  const slugCharLookUpTable = reduceLookUpTable(lookup);
  const replacedSlug = replaceSlug(slug, slugCharLookUpTable, exceptions);
  const tags = topicTags.length ? topicTags : categoryTags;

  const tagsArray = tags.map(tag => tag.displayName || tag.name);
  const sortedTags = sortByMatch(tagsArray, queryCategories);

  let imageComponent;

  if (articleThumbnailImage && articleThumbnailImage !== null) {
    const {
      image: {
        title,
        description,
        gatsbyImageData,
        file: {
          details: {
            image: { height: imageHeight, width: imageWidth },
          },
        },
      },
      focalPoint: {
        focalPoint: { x: focalPointX, y: focalPointY },
      },
    } = articleThumbnailImage;

    const xCoordinate = (focalPointX / imageWidth) * 100;
    const yCoordinate = (focalPointY / imageHeight) * 100;

    imageComponent = (
      <ImageContainer>
        <TagPillWrapper direction={direction}>
          <Pill
            text={contentType?.displayName}
            appearance={contentType?.isDefault ? 'primary' : 'alt1'}
          />
        </TagPillWrapper>
        <GatsbyImage
          image={getImage({gatsbyImageData: gatsbyImageData }) as IGatsbyImageData}
          imgStyle={{
            objectFit: 'cover',
            objectPosition: `${xCoordinate}% ${yCoordinate}%`,
          }}
          css={focalPointImageStyle}
          alt={description || title}
        />
      </ImageContainer>
    );
  } else if (mainImage && mainImage !== null) {
    imageComponent = (
      <ImageContainer>
        <TagPillWrapper direction={direction}>
          <Pill
            text={contentType?.displayName}
            appearance={contentType?.isDefault ? 'primary' : 'alt1'}
          />
        </TagPillWrapper>
        <GatsbyImage
          image={getImage(mainImage) as IGatsbyImageData}
          css={imageBlock}
          alt={mainImage.description || mainImage.title}
        />
      </ImageContainer>
    );
  }

  const splitReplacedSlug = replacedSlug.split('/');
  const updatedSlug =
    splitReplacedSlug.length === 2
      ? splitReplacedSlug[1]
      : splitReplacedSlug[0];

  return (
    <ArticleWrapper direction={direction}>
      <div style={{ display: 'none' }}>viewCount: {viewCount}</div>
      <Link
        to={`/${indexSlug}/${updatedSlug}`}
        title={name}
        data-testid={`article-${updatedSlug}`}
        data-analytics-element={name}
        data-analytics-category="article"
        data-analytics-context={seoTitle}
        onClick={() =>
          fireArticleClickEvent({ appliedFilters, contentName: name })
        }
      >
        <Container
          elevation
          appearance={backgroundColor === 'white' ? 'primary' : 'secondary'}
        >
          <SimpleGrid
            columns={{
              sm: 2,
              md: 2,
            }}
            spacing={3}
          >
            <TextBlock direction={direction}>
              <TagsBarWrapper>
                {sortedTags.map(tag => (
                  <div className="tags" key={tag}>
                    {tag}
                  </div>
                ))}
              </TagsBarWrapper>
              <div>
                <Heading level={4} weight={3}>
                  {name}
                </Heading>
                <p className="summary">{summary}</p>
              </div>
              <InfoWrapper>
                <IconWithTextWrapper>
                  <Icon name="calendar-month" size={3} />
                  <p style={{ direction: 'ltr' }}>{publishingDate}</p>
                </IconWithTextWrapper>
                {readingTime > 0 && (
                  <IconWithTextWrapper>
                    <Icon name="clock" size={3} />
                    <p>{readingTime} min</p>
                  </IconWithTextWrapper>
                )}
              </InfoWrapper>
            </TextBlock>

            {imageComponent}
          </SimpleGrid>
        </Container>
      </Link>
    </ArticleWrapper>
  );
};
